import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Grid,
  Chip,
  Pagination,
  TableSortLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search, Block, CheckCircle, Refresh, Info, Close, Person, Phone } from '@mui/icons-material';
import axios from '../../../resources/axios';
import moment from 'moment';

interface UserDevice {
  _id: string;
  user: string;
  deviceId: string;
  deviceInfo: string;
  simCardNumber: string;
  banned: boolean;
  lastActive: string;
  createdAt: string;
  updatedAt: string;
}

interface UserData {
  _id: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  phone: string;
  email?: string;
}

interface PaginationData {
  limit: number;
  page: number;
  totalPages: number;
  count: number;
}

type OrderDirection = 'asc' | 'desc';

const UserDevicesTab = () => {
  const theme = useTheme();
  const [data, setData] = useState<UserDevice[]>([]);
  const [usersMap, setUsersMap] = useState<Record<string, UserData>>({});
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'userId' | 'deviceId'>('userId');
  const [selectedDevice, setSelectedDevice] = useState<UserDevice | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    limit: 20,
    page: 1,
    totalPages: 1,
    count: 0
  });
  const [orderBy, setOrderBy] = useState<keyof UserDevice>('lastActive');
  const [order, setOrder] = useState<OrderDirection>('desc');

  const getUserName = (user?: UserData) => {
    if (!user) return 'Невідомий користувач';
    const parts = [user.lastName, user.firstName, user.middleName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Користувач без імені';
  };

  const getUserInitial = (user?: UserData) => {
    if (!user) return '?';
    if (user.firstName) return user.firstName.charAt(0).toUpperCase();
    if (user.lastName) return user.lastName.charAt(0).toUpperCase();
    return 'К';
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const fetchUsers = async (userIds: string[]) => {
    if (userIds.length === 0) return;
    
    setUsersLoading(true);
    try {
      const uniqueIds = Array.from(new Set(userIds));
      const idsToFetch = uniqueIds.filter(id => !usersMap[id]);
      
      if (idsToFetch.length === 0) {
        setUsersLoading(false);
        return;
      }
      
      const userRequests = idsToFetch.map(id => 
        axios.get(`/users/${id}`).catch(err => {
          console.error(`Error fetching user ${id}:`, err);
          return null;
        })
      );
      
      const responses = await Promise.all(userRequests);
      const newUsersMap: Record<string, UserData> = {};
      
      responses.forEach((response, index) => {
        if (response && response.data?.user) {
          newUsersMap[idsToFetch[index]] = response.data.user;
        }
      });
      
      setUsersMap(prev => ({ ...prev, ...newUsersMap }));
    } catch (err: any) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchDevices = async (page: number = 1) => {
    setLoading(true);
    setError('');

    try {
      const params: any = { page, limit: 20 };
      const query = searchQuery.trim();
      
      if (query) {
        params[searchType] = query;
      }

      const response = await axios.get('/admins/user_devices', { params });

      const devices = response.data.data.userDevices;
      setData(devices);
      setPagination(response.data.pagination);

      const userIds = devices.map((device: UserDevice) => device.user);
      await fetchUsers(userIds);
    } catch (err: any) {
      console.error('Error fetching user devices:', err);
      setError(err.response?.data?.message || 'Помилка отримання даних');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchDevices(1);
  };

  const handleBanToggle = async (deviceData: UserDevice) => {
    try {
      const response = await axios.put(`/admins/user_devices/${deviceData._id}`, {
        banned: !deviceData.banned
      });

      setData(prevData =>
        prevData.map(item =>
          item._id === deviceData._id ? response.data.data : item
        )
      );

      if (selectedDevice?._id === deviceData._id) {
        setSelectedDevice(response.data.data);
      }
    } catch (err: any) {
      console.error('Error updating device status:', err);
      setError(err.response?.data?.message || 'Помилка зміни статусу пристрою');
    }
  };

  const handleOpenDetails = (device: UserDevice) => {
    setSelectedDevice(device);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedDevice(null);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    fetchDevices(value);
  };

  const handleSort = (property: keyof UserDevice) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (aValue < bValue) {
      return order === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  });

  useEffect(() => {
    fetchDevices();
  }, []);

  return (
    <Box>
      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : theme.palette.background.paper,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          📱 Девайси користувачів
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0',
            mb: 2.5
          }}
        >
          Моніторинг та управління пристроями користувачів. Ви можете заблокувати пару 'користувач - пристрій' для запобігання зловживанням.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Тип пошуку</InputLabel>
              <Select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as 'userId' | 'deviceId')}
                label="Тип пошуку"
              >
                <MenuItem value="userId">User ID</MenuItem>
                <MenuItem value="deviceId">Device ID</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              label="Пошук"
              placeholder={searchType === 'userId' ? 'Введіть User ID' : 'Введіть Device ID'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{
                backgroundColor: '#5073F8',
                '&:hover': { backgroundColor: '#3d5dd6' },
                height: '40px'
              }}
            >
              Пошук
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0' }}>
            Знайдено пристроїв: <strong>{pagination.count}</strong>
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => fetchDevices(pagination.page)}
            disabled={loading}
            size="small"
          >
            Оновити
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && data.length > 0 && (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}>№</TableCell>
                    <TableCell>
                      👤 Користувач
                    </TableCell>
                    <TableCell>
                      📞 Телефон
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'lastActive'}
                        direction={orderBy === 'lastActive' ? order : 'asc'}
                        onClick={() => handleSort('lastActive')}
                      >
                        ⏰ Остання активність
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Статус</TableCell>
                    <TableCell align="center">Дії</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((row, index) => {
                    const user = usersMap[row.user];
                    const userName = getUserName(user);
                    const userInitial = getUserInitial(user);
                    
                    return (
                      <TableRow
                        key={row._id}
                        sx={{
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' 
                              ? 'rgba(0, 0, 0, 0.04)' 
                              : 'rgba(255, 255, 255, 0.08)'
                          }
                        }}
                      >
                        <TableCell sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0' }}>
                          {(pagination.page - 1) * pagination.limit + index + 1}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {usersLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <>
                                <Avatar
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: getAvatarColor(userName),
                                    fontSize: '1rem',
                                    fontWeight: 600
                                  }}
                                >
                                  {userInitial}
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {userName}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user?.phone || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {moment(row.lastActive).format('DD.MM.YYYY HH:mm')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.mode === 'light' ? '#9E9E9E' : '#757575' }}>
                            {moment(row.lastActive).fromNow()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.banned ? 'ЗАБЛОКОВАНО' : 'Активний'}
                            size="small"
                            sx={{
                              backgroundColor: row.banned ? '#F44336' : '#4CAF50',
                              color: '#FFFFFF',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="Детальна інформація">
                              <IconButton
                                onClick={() => handleOpenDetails(row)}
                                sx={{
                                  color: '#5073F8',
                                  '&:hover': {
                                    backgroundColor: 'rgba(80, 115, 248, 0.1)'
                                  }
                                }}
                                size="small"
                              >
                                <Info />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={row.banned ? 'Розблокувати' : 'Заблокувати'}>
                              <IconButton
                                onClick={() => handleBanToggle(row)}
                                sx={{
                                  color: row.banned ? '#4CAF50' : '#F44336',
                                  '&:hover': {
                                    backgroundColor: row.banned 
                                      ? 'rgba(76, 175, 80, 0.1)' 
                                      : 'rgba(244, 67, 54, 0.1)'
                                  }
                                }}
                                size="small"
                              >
                                {row.banned ? <CheckCircle /> : <Block />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {!loading && data.length === 0 && (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              color: theme.palette.mode === 'light' ? '#9E9E9E' : '#757575'
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              📱 Пристрої не знайдено
            </Typography>
            <Typography variant="body2">
              Спробуйте змінити критерії пошуку або завантажити всі пристрої
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : theme.palette.background.paper
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            📱 Детальна інформація про пристрій
          </Typography>
          <IconButton onClick={handleCloseDetails} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 3 }}>
          {selectedDevice && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                  👤 Користувач
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: getAvatarColor(getUserName(usersMap[selectedDevice.user])),
                      fontSize: '1.2rem',
                      fontWeight: 600
                    }}
                  >
                    {getUserInitial(usersMap[selectedDevice.user])}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getUserName(usersMap[selectedDevice.user])}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.mode === 'light' ? '#9E9E9E' : '#757575', fontFamily: 'monospace' }}>
                      ID: {selectedDevice.user}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                  📞 Контакти
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Телефон: <strong>{usersMap[selectedDevice.user]?.phone || '-'}</strong>
                </Typography>
                {usersMap[selectedDevice.user]?.email && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Email: <strong>{usersMap[selectedDevice.user].email}</strong>
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                  📱 Інформація про пристрій
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Device ID: <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#5073F8' }}>{selectedDevice.deviceId}</Typography>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Device Info: <strong>{selectedDevice.deviceInfo || '-'}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  SIM Card: <strong>{selectedDevice.simCardNumber || '-'}</strong>
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                  ⏰ Активність
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Остання активність: <strong>{moment(selectedDevice.lastActive).format('DD.MM.YYYY HH:mm')}</strong> ({moment(selectedDevice.lastActive).fromNow()})
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Перша активність: <strong>{moment(selectedDevice.createdAt).format('DD.MM.YYYY HH:mm')}</strong>
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                  🔒 Статус
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={selectedDevice.banned ? 'ЗАБЛОКОВАНО' : 'Активний'}
                    sx={{
                      backgroundColor: selectedDevice.banned ? '#F44336' : '#4CAF50',
                      color: '#FFFFFF',
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        
        <Divider />
        
        <DialogActions sx={{ p: 2.5 }}>
          {selectedDevice && (
            <Button
              variant="contained"
              startIcon={selectedDevice.banned ? <CheckCircle /> : <Block />}
              onClick={() => {
                handleBanToggle(selectedDevice);
              }}
              sx={{
                backgroundColor: selectedDevice.banned ? '#4CAF50' : '#F44336',
                '&:hover': {
                  backgroundColor: selectedDevice.banned ? '#45a049' : '#da190b'
                }
              }}
            >
              {selectedDevice.banned ? 'Розблокувати пристрій' : 'Заблокувати пристрій'}
            </Button>
          )}
          <Button onClick={handleCloseDetails} variant="outlined">
            Закрити
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDevicesTab;
